
/**
 * Get currency symbol based on currency code
 * @param currencyCode ISO 4217 currency code (e.g. EUR, USD, HTG)
 * @returns Currency symbol (e.g. €, $, G)
 */
export const getCurrencySymbol = (currencyCode: string = 'EUR'): string => {
  switch (currencyCode) {
    case "EUR": return "€";
    case "USD": return "$";
    case "HTG": return "G";
    case "DOP": return "RD$";
    case "GBP": return "£";
    case "CAD": return "CA$";
    case "CHF": return "Fr";
    case "AUD": return "A$";
    case "JPY": return "¥";
    case "CNY": return "¥";
    case "RUB": return "₽";
    case "BRL": return "R$";
    case "MXN": return "Mex$";
    case "INR": return "₹";
    case "MAD": return "DH";
    case "XAF": return "FCFA";
    case "XOF": return "CFA";
    case "NGN": return "₦";
    case "ZAR": return "R";
    case "KES": return "KSh";
    default: return currencyCode;
  }
};

/**
 * Format price with the correct currency symbol
 * @param price Price value
 * @param currencyCode ISO 4217 currency code (e.g. EUR, USD, HTG)
 * @returns Formatted price with currency symbol
 */
export const formatPrice = (price: number, currencyCode: string = 'EUR'): string => {
  const symbol = getCurrencySymbol(currencyCode);
  
  // For currencies that show symbol after the amount (like FCFA)
  if (["XAF", "XOF"].includes(currencyCode)) {
    return `${price.toFixed(2)} ${symbol}`;
  }
  
  // For all other currencies
  return `${symbol}${price.toFixed(2)}`;
};

/**
 * Format price using Intl.NumberFormat for locale-aware formatting
 * @param price Price value
 * @param currencyCode ISO 4217 currency code (e.g. EUR, USD, HTG)
 * @param locale Locale for formatting (default: fr-FR)
 * @returns Locale-formatted price with currency
 */
export const formatPriceWithIntl = (
  price: number, 
  currencyCode: string = 'EUR',
  locale: string = 'fr-FR'
): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol'
    }).format(price);
  } catch (error) {
    // Fallback if Intl formatting fails
    return formatPrice(price, currencyCode);
  }
};
